
import { UploadOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { Form, Space, Button, Typography, Input, Upload, Checkbox, Row, Col, Select, InputNumber, FormProps } from 'antd';
import { setMessages } from '@/redux/slices/app.slice';

type PhysicalActivityModalProps = {
    onCancel: () => void;
    physicalActivityID?: number;
};
import {
    getListPhysicalActivitiesByIntensity,
    watchAgePhysicalActivity,
    getListSuitableObjectPhysicalActivity,
    watchSuitableObjectPhysicalActivity,
    watchTypePhysicalActivity,
    watchIntensityPhysicalActivity,
    createPhysicalActivityByIntensity,
    watchPhysicalActivityMessage,
    watchListDetailPhysicalActivityByIntensity,
    updatePhysicalActivityByIntensity,
    watchPhysicalActivityLoading,
    resetMessages
} from '@/redux/slices/regulate-level-physical-activity.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useEffect } from 'react';

type AgeSelectOption = {
    value: number;
    label: string;
    healthStatusBMIType: string;
};

export const PhysicalActivityModal = ({
    onCancel,
    physicalActivityID
}: PhysicalActivityModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const listSuitableObjectPhysicalActivity = useAppSelector(watchSuitableObjectPhysicalActivity);
    const listTypePhysicalActivity = useAppSelector(watchTypePhysicalActivity);
    const listIntensityPhysicalActivity = useAppSelector(watchIntensityPhysicalActivity);
    const messageInfo = useAppSelector(watchPhysicalActivityMessage);
    const detailPhysicalActivityByIntensity = useAppSelector(watchListDetailPhysicalActivityByIntensity);
    const loading = useAppSelector(watchPhysicalActivityLoading);

    const listAgePhysicalActivity = useAppSelector(watchAgePhysicalActivity);

    const listAgeActivityOptions = listAgePhysicalActivity.map((item) => ({
        value: item.ID,
        label: item.DESCRIPTION,
        healthStatusBMIType: item.HEALTH_STATUS_BMI_TYPE,
    }));
    const listSuitableObjectActivityOptions = listSuitableObjectPhysicalActivity.map((item) => ({
        value: item.id,
        label: item.description
    }));
    const listTypePhysicalActivityOptions = listTypePhysicalActivity.map((item) => ({
        value: item.id,
        label: item.name
    }));
    const listIntensityPhysicalActivityOptions = listIntensityPhysicalActivity.map((item) => ({
        value: item.id,
        label: item.name
    }));

    const ALLOWED_TYPES = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/svg+xml'
    ];

    const beforeUpload = (file: File) => {
        const isValidType = ALLOWED_TYPES.includes(file.type);
        if (!isValidType) {
            return Upload.LIST_IGNORE;
        }

        const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
        const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            return Upload.LIST_IGNORE;
        }

        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            return Upload.LIST_IGNORE;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const newFile = {
                uid: Date.now().toString(),
                name: file.name,
                url: reader.result,
                status: 'done',
            };
            form.setFieldValue('IMAGE', newFile.url);
        };
        return false;
    };

    const uploadImage = async (image: string, type?: string) => {
        const thumbnail = {
            'thumbnail': image,
            'type': type
        }
        const response = await apiFetcher.post('upload-image', thumbnail);
        return response.data;
    };

    const onFinish: FormProps<CreatePhysicalActivityByIntensity>['onFinish'] = async (values) => {
        const isImageUrl = (value: string | undefined) => {
            return typeof value === 'string' && value.startsWith('http');
        };
        const imageUrl = values.IMAGE ? isImageUrl(values.IMAGE)
            ? values.IMAGE
            : await uploadImage(values.IMAGE, 'physical_activities') : "";
        values.IMAGE = imageUrl;
        const formData = new FormData();
        Object.entries(values).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            formData.append(key, Array.isArray(value) ? JSON.stringify(value) : value.toString());
        })
        if (physicalActivityID) {
            await dispatch(updatePhysicalActivityByIntensity({ id: physicalActivityID, payload: formData }));
        } else {
            await dispatch(createPhysicalActivityByIntensity(formData));
        }
        await dispatch(getListPhysicalActivitiesByIntensity());
        onCancel();
    };

    const chooseAge = ({ healthStatusBMIType }: AgeSelectOption) => {
        dispatch(getListSuitableObjectPhysicalActivity({ type: healthStatusBMIType, option: 'PHYSICAL_ACTIVITY' }));
    };

    useEffect(() => {
        const fetchAndSetForm = async () => {
            if (physicalActivityID && detailPhysicalActivityByIntensity) {
                await dispatch(
                    getListSuitableObjectPhysicalActivity(
                        { type: detailPhysicalActivityByIntensity.HEALTH_STATUS_BMIS?.[0]?.TYPE || '', option: 'PHYSICAL_ACTIVITY' }
                    )
                );
                form.setFieldsValue({
                    ...detailPhysicalActivityByIntensity,
                    HEALTH_STATUS_BMI_IDS: detailPhysicalActivityByIntensity.HEALTH_STATUS_BMIS?.map(item => item.ID),
                });
            }
        };

        fetchAndSetForm();
    }, [detailPhysicalActivityByIntensity, dispatch, form, physicalActivityID]);

    useEffect(() => {
        if (messageInfo) {
            dispatch(
                setMessages([
                    {
                        type: messageInfo.type,
                        message: messageInfo.message,
                        description: messageInfo.description
                    }
                ])
            );
            dispatch(resetMessages());
        }
    }, [dispatch, messageInfo]);

    return (
        <div css={rootStyles}>
            <div css={boxStyles}>
                <Form
                    form={form}
                    layout="horizontal"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    onFinish={onFinish}
                >
                    <Typography.Title css={titleStyles} level={3}>
                        Tự tạo hoạt động thể lực
                    </Typography.Title>
                    <Form.Item
                        label='Độ tuổi'
                        name='AGE_RANGE_ID'
                        rules={[{ required: true, message: 'Vui lòng chọn độ tuổi' }]}
                        css={formStyles}
                    >
                        <Select
                            placeholder='Chọn độ tuổi'
                            options={listAgeActivityOptions}
                            onSelect={(_value, option) => chooseAge(option)}
                        />
                    </Form.Item>
                    <Form.Item
                        label='Tên hoạt động thể lực'
                        name='NAME'
                        css={formStyles}
                        rules={[{ required: true, message: 'Vui lòng nhập tên hoạt động thể lực' }]}
                    >
                        <Input
                            placeholder='Nhập tên hoạt động thể lực'
                            maxLength={200}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.length > 200) {
                                    e.target.value = value.slice(0, 200);
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label='Mô tả (nếu có)'
                        name='DESCRIPTION'
                        css={formStyles}
                    >
                        <Input.TextArea
                            rows={2}
                            size='large'
                            placeholder='Nhập mô tả cho hoạt động thể lực (nếu có)'
                            maxLength={1000}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.length > 1000) {
                                    e.target.value = value.slice(0, 1000);
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name={'IMAGE'}
                        label={'Ảnh thumbnail'}
                        labelCol={{ span: 8 }}
                        getValueFromEvent={(e) => {
                            return Array.isArray(e) ? e.slice(0, 1) : e?.fileList?.slice(0, 1);
                        }}
                        rules={[{ required: true, message: 'Vui lòng chọn ảnh' }]}
                        css={formStyles}
                    >
                        <Upload
                            beforeUpload={beforeUpload}
                            listType='picture'
                            accept='.png, .jpeg, .jpg'
                            onChange={(value) => {
                                const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(value.file.type!);

                                if (!isImage) {
                                    return false;
                                }
                            }}
                            maxCount={1}
                            defaultFileList={
                                physicalActivityID && detailPhysicalActivityByIntensity?.IMAGE
                                    ? [
                                        {
                                            uid: '-1',
                                            name: detailPhysicalActivityByIntensity.NAME || 'image',
                                            url: detailPhysicalActivityByIntensity.IMAGE,
                                            status: 'done',
                                        },
                                    ]
                                    : []
                            }
                        >
                            <Button icon={<UploadOutlined />}>
                                Chọn để tải lên hình ảnh
                            </Button>
                        </Upload>
                    </Form.Item>
                    <Form.Item
                        label='Loại hình hoạt động thể lực'
                        name='PHYSICAL_ACTIVITY_TYPE_ID'
                        rules={[{ required: true, message: 'Vui lòng chọn loại hình hoạt thể lực' }]}
                        css={formStyles}
                    >
                        <Select
                            placeholder='Chọn loại hình hoạt động thể lực'
                            options={listTypePhysicalActivityOptions}
                        />
                    </Form.Item>
                    <Form.Item
                        label='Cường độ hoạt động thể lực'
                        name='PHYSICAL_ACTIVITY_LEVEL'
                        rules={[{ required: true, message: 'Vui lòng chọn cường độ hoạt động thể lực' }]}
                        css={formStyles}
                    >
                        <Select
                            placeholder='Chọn loại cường độ hoạt động thể lực (Nhẹ, trung bình, cao)'
                            options={listIntensityPhysicalActivityOptions} />
                    </Form.Item>
                    <Form.Item
                        label='Hệ số MET hoạt động thể lực'
                        name='MET_FACTOR'
                        rules={[{ required: true, message: 'Vui lòng nhập hệ số MET' }]}
                        css={formStyle}
                    >
                        <InputNumber<number>
                            decimalSeparator="."
                            min={0}
                            formatter={(value) => (value ?? "").toString().replace(/,/g, ".")}
                            placeholder='Nhập hệ số MET'
                            suffix='ML02/kg/phút'

                        />
                    </Form.Item>
                    {listSuitableObjectActivityOptions.length > 0 &&
                        <Form.Item
                            label="Đối tượng phù hợp với hoạt động thể lực"
                            name="HEALTH_STATUS_BMI_IDS"
                            rules={[{ required: true, message: 'Vui lòng chọn đối tượng' }]}
                            extra="(Chọn đối tượng có tình trạng bệnh lý phù hợp với hoạt động thể lực)"
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                        >
                            <Checkbox.Group>
                                <Row gutter={[0, 8]}>
                                    {listSuitableObjectActivityOptions.map(option => (
                                        <Col span={24} key={option.value}>
                                            <Checkbox value={option.value}>{option.label}</Checkbox>
                                        </Col>
                                    ))}
                                </Row>
                            </Checkbox.Group>
                        </Form.Item>
                    }

                    <div>
                        <Space css={groupButtonStyles}>
                            <Button type='default' size='large' onClick={onCancel}>
                                Hủy
                            </Button>
                            <Button
                                type='primary'
                                size='large'
                                htmlType='submit'
                                loading={loading}
                            >
                                Lưu
                            </Button>
                        </Space>
                    </div>

                </Form>
            </div>
        </div>
    );
};

const rootStyles = css`
  background: var(--white-soft-color);
  display: flex;
  flex-direction: column;
  .ant-form-item {
    margin-bottom: 1rem !important;
  }
`;

const groupButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;

  button {
    padding: 2rem 4rem;
  }
  margin-bottom: 0;
  
`;

const titleStyles = css`
  margin-bottom: 1.6rem !important;
  line-height: 3.2rem !important;
`;

const boxStyles = css`
    .ant-form-item-control-input-content {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }
    .ant-form-item-label {
        label {
            width: 100%;
        }
    }
    
    .ant-form-item .ant-form-item-label > label {
        font-size: initial !important;
    } 

`

const formStyles = css`
  .ant-form-item-control-input-content>.ant-input {
    height: auto !important;  
    font-size: initial !important;
  }
`;

const formStyle = css`
    .ant-input-affix-wrapper >input.ant-input {
        font-size: unset !important;
    }
    .ant-input-affix-wrapper {
        font-size: unset !important;
    }
    .ant-input-number-affix-wrapper {
        width: 100% !important;
    }
`